"use client";

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { Copy, Check } from "lucide-react";
import { copyTextToClipboard } from "@/lib/clipboard";
import { useI18n } from "./I18nProvider";

export function MarkdownInlineCode({ value }: { value: string }) {
  const { t } = useI18n();
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const popup = useRef<HTMLSpanElement>(null);
  const action = useRef<HTMLButtonElement>(null);
  const busy = useRef(false);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<"idle" | "copied" | "failed">("idle");

  useLayoutEffect(() => {
    const element = popup.current;
    const anchor = trigger.current;
    if (!open || !element || !anchor) return;
    const position = () => {
      const rect = anchor.getBoundingClientRect();
      const menu = element.getBoundingClientRect();
      element.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - menu.width - 8))}px`;
      element.style.top = `${Math.max(8, rect.bottom + menu.height + 8 <= window.innerHeight ? rect.bottom + 6 : rect.top - menu.height - 6)}px`;
    };
    position();
    const observer = new ResizeObserver(position);
    observer.observe(element);
    return () => observer.disconnect();
  }, [open, status]);

  useEffect(() => {
    if (!open) return;
    action.current?.focus({ preventScroll: true });
    const close = () => popup.current?.hidePopover();
    window.addEventListener("resize", close);
    window.addEventListener("scroll", close, true);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [open]);

  useEffect(() => {
    if (status !== "copied") return;
    const timer = window.setTimeout(() => {
      const restoreFocus = popup.current?.contains(document.activeElement);
      popup.current?.hidePopover();
      if (restoreFocus) trigger.current?.focus({ preventScroll: true });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [status]);

  async function copy() {
    if (busy.current) return;
    busy.current = true;
    setStatus("idle");
    const copied = await copyTextToClipboard(value);
    setStatus(copied ? "copied" : "failed");
    busy.current = false;
  }

  return (
    <>
      <button ref={trigger} type="button" className="markdown-inline-copy-trigger"
        popoverTarget={id} aria-expanded={open} aria-controls={id}
        onClick={(event) => {
          const selection = window.getSelection();
          if (selection && !selection.isCollapsed && trigger.current && selection.containsNode(trigger.current, true)) {
            event.preventDefault();
            return;
          }
          if (!popup.current?.matches(":popover-open")) setStatus("idle");
        }}><code>{value}</code></button>
      <span ref={popup} id={id} popover="auto" className="markdown-inline-copy-popover"
        onToggle={(event) => setOpen(event.newState === "open")}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            popup.current?.hidePopover();
            trigger.current?.focus({ preventScroll: true });
          }
        }}>
        <button ref={action} type="button" className="markdown-code-copy" onClick={copy}>
          {status === "copied" ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
          <span aria-live="polite">{t(status === "copied" ? "invite.copied" : status === "failed" ? "share.copyFailed" : "invite.copy")}</span>
        </button>
      </span>
    </>
  );
}
